import useProfileWindowSettings from "../../profileWindowSettings/useProfileWindowSettings";
import React from "react";
import { Badge, Box, HStack, Text, VStack } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { auth } from "../../../core/auth";
import EntityProfileSectionCard from "../../entityProfileModal/components/EntityProfileSectionCard";
import { EntityProfileLoadingState } from "../../entityProfileModal/components/EntityProfileFeedback";
import {
  entityProfileModalBorders,
  entityProfileModalColors,
} from "../../entityProfileModal/entityProfileModal.theme";
import type { PatientProfileBridgeActions } from "../patientProfileModal.actions";
import { PatientProfileDisplayData } from "../patientProfileModal.mocks";
import { PatientProfileSection } from "../patientProfileModal.sections";
import { PatientProfilePatient } from "../patientProfileModal.types";
import { PatientId } from "../../../shared/schema/schema";
import PatientProfileAddressesSection from "../sections/profile/addresses/PatientProfileAddressesSection";
import PatientProfileAvailabilitySection from "../sections/profile/availability/PatientProfileAvailabilitySection";
import PatientProfileCaregiverRelationshipsSection from "../sections/profile/relationships/PatientProfileCaregiverRelationshipsSection";
import PatientProfileEmergencyPreparednessSection from "../sections/profile/emergencyPreparedness/PatientProfileEmergencyPreparednessSection";
import PatientProfileFaxesSection from "../sections/profile/faxes/PatientProfileFaxesSection";
import PatientProfileInformationSection from "../sections/profile/information/PatientProfileInformationSection";
import PatientProfilePatientContactsSection from "../sections/profile/patientContacts/PatientProfilePatientContactsSection";
import PatientProfileStatusReviewSection from "../sections/profile/statusReview/PatientProfileStatusReviewSection";
import PatientProfileStaffingPreferencesSection from "../sections/profile/staffingPreferences/PatientProfileStaffingPreferencesSection";
import PatientProfileSummarySection from "../sections/profile/summary/PatientProfileSummarySection";
import PatientProfileCalendarCaregiversSection from "../sections/calendar/caregivers/PatientProfileCalendarCaregiversSection";
import PatientProfileCalendarSection, {
  PatientAuthorizationDetailsTable,
} from "../sections/calendar/visits/PatientProfileCalendarSection";
import PatientProfileWeeklyTemplateSection from "../sections/calendar/weeklyTemplate/PatientProfileWeeklyTemplateSection";
import { usePatientProfileWeeklyTemplate } from "../sections/calendar/weeklyTemplate/PatientProfileWeeklyTemplateSection.hooks";
import PatientProfileContractsSection from "../sections/administrative/contracts/PatientProfileContractsSection";
import PatientProfileDiagnosisCodesSection from "../sections/administrative/diagnosisCodes/PatientProfileDiagnosisCodesSection";
import PatientProfilePayRatesSection from "../sections/administrative/payRates/PatientProfilePayRatesSection";
import PatientProfileSurplusesSection from "../sections/administrative/surpluses/PatientProfileSurplusesSection";
import PatientProfileBillingInvoicesSection from "../sections/billing/invoices/PatientProfileBillingInvoicesSection";
import PatientProfileMedicalSection from "../sections/medical/PatientProfileMedicalSection";
import PatientProfileRecentActivitySection from "../sections/recentActivity/PatientProfileRecentActivitySection";
import EntityModalButton from "../../entityProfileModal/components/EntityModalButton";

type Props = {
  actions: PatientProfileBridgeActions;
  displayData: PatientProfileDisplayData;
  isProfileLoading: boolean;
  patient?: PatientProfilePatient;
  patientId: PatientId;
  section: PatientProfileSection;
  showBillingFeature: boolean;
};

const profileBackedSubsectionIds = new Set([
  "summary",
  "addresses",
  "emergency-contact-preparedness",
  "patient-contacts",
  "information",
]);

function PlaceholderSection() {
  return (
    <Box
      bg={entityProfileModalColors.surfaceMuted}
      border={entityProfileModalBorders.panel}
      borderRadius="2px"
      px={4}
      py={5}
    >
      <Text color={entityProfileModalColors.muted}>Pending</Text>
    </Box>
  );
}

export default function PatientProfileSections(props: Props) {
  const { actions } = props;
  const queryClient = useQueryClient();
  const [{ saveCalendarSelection }] = useProfileWindowSettings();
  const calendarCards = React.useRef<Props["section"]["subsections"]>([]);
  if (!saveCalendarSelection) {
    calendarCards.current = [];
  } else if (props.section.label === "Calendar") {
    calendarCards.current = props.section.subsections.filter(
      (subsection) => subsection.id === "calendar" || subsection.id === "list-view"
    );
  }
  const subsections =
    props.section.label === "Calendar"
      ? props.section.subsections
      : [...calendarCards.current, ...props.section.subsections];
  const weeklyTemplateQuery = usePatientProfileWeeklyTemplate(
    props.patientId,
    props.section.label === "Calendar"
  );
  const patientContracts = props.patient?.contracts ?? [];

  const getSectionActions = (subsectionId: string) => {
    if (subsectionId !== "status-review" || props.patient === undefined) {
      return null;
    }
    const patient = props.patient;

    return (
      <EntityModalButton
        iconClassName="fa fa-refresh"
        kind="blue"
        size="sm"
        onClick={() =>
          actions.openChangeStatus({
            patient,
            onDone: () => {
              queryClient.invalidateQueries({
                queryKey: ["patientProfileModal", props.patientId, "statusChanges"],
              });
              queryClient.invalidateQueries({ queryKey: ["patient", "get", props.patientId] });
            },
          })
        }
      >
        Change Status
      </EntityModalButton>
    );
  };

  const getSectionTitle = (subsectionId: string, fallback: string) => {
    if (subsectionId === "calendar") {
      return "Patient Calendar";
    }
    return fallback;
  };

  const getSectionTitleBadge = (subsectionId: string) => {
    if (subsectionId !== "calendar") {
      return null;
    }
    const showMissingTemplate =
      props.patient?.status === "ACTIVE" && weeklyTemplateQuery.data?.shifts.length === 0;
    if (!showMissingTemplate) {
      return null;
    }
    return (
      <Badge
        bg="#d9534f"
        borderRadius="4px"
        color="#fff"
        fontSize="11px"
        fontWeight={500}
        px="6px"
        py="3px"
        textTransform="none"
      >
        Missing weekly template
      </Badge>
    );
  };
  const refreshPatient = () => {
    queryClient.invalidateQueries({ queryKey: ["patient", "get", props.patientId] });
    queryClient.invalidateQueries({
      queryKey: ["patientProfileModal", props.patientId, "authorizations"],
    });
  };
  const canEditAuthorizations = auth.isAllowedTo("edit_patient_page_authorizations");
  const renderAddAuthorizationButton = () => {
    const patient = props.patient;

    if (!canEditAuthorizations || patient === undefined) {
      return null;
    }

    return (
      <HStack justifyContent="flex-end">
        <EntityModalButton
          kind="blue"
          size="sm"
          onClick={() => actions.openAuthorization({ patient, onDone: refreshPatient })}
        >
          Add authorization
        </EntityModalButton>
      </HStack>
    );
  };

  const isCalendarCardHidden = (subsection: Props["section"]["subsections"][number]) =>
    props.section.label !== "Calendar" && calendarCards.current.includes(subsection);

  return (
    <>
      {subsections.map((subsection) => (
        <EntityProfileSectionCard
          key={subsection.id}
          actions={getSectionActions(subsection.id)}
          entity="patient"
          id={subsection.id}
          idPrefix="react-patient-section"
          isHidden={isCalendarCardHidden(subsection)}
          title={getSectionTitle(subsection.id, subsection.label)}
          titleBadge={getSectionTitleBadge(subsection.id)}
        >
          {props.isProfileLoading && profileBackedSubsectionIds.has(subsection.id) ? (
            <EntityProfileLoadingState label="Loading profile details..." />
          ) : subsection.id === "calendar" ? (
            <PatientProfileCalendarSection
              actions={actions}
              isHidden={isCalendarCardHidden(subsection)}
              patient={props.patient}
              patientId={props.patientId}
              showBillingFeature={props.showBillingFeature}
              view="calendar"
            />
          ) : subsection.id === "list-view" ? (
            <PatientProfileCalendarSection
              actions={actions}
              isHidden={isCalendarCardHidden(subsection)}
              patient={props.patient}
              patientId={props.patientId}
              showBillingFeature={props.showBillingFeature}
              view="list"
            />
          ) : subsection.id === "weekly-template" ? (
            <PatientProfileWeeklyTemplateSection
              actions={actions}
              patientId={props.patientId}
              patientStatus={props.patient?.status}
            />
          ) : subsection.id === "caregivers" ? (
            <PatientProfileCalendarCaregiversSection
              actions={actions}
              patientId={props.patientId}
            />
          ) : subsection.id === "summary" ? (
            <PatientProfileSummarySection
              actions={actions}
              displayData={props.displayData}
              patient={props.patient as PatientProfilePatient}
              showBillingFeature={props.showBillingFeature}
            />
          ) : subsection.id === "status-review" ? (
            <PatientProfileStatusReviewSection
              contracts={patientContracts}
              patientId={props.patientId}
            />
          ) : subsection.id === "staffing-preferences" ? (
            <PatientProfileStaffingPreferencesSection
              patient={props.patient}
              patientId={props.patientId}
            />
          ) : subsection.id === "availability" ? (
            <PatientProfileAvailabilitySection patientId={props.patientId} />
          ) : subsection.id === "addresses" ? (
            <PatientProfileAddressesSection patient={props.patient as PatientProfilePatient} />
          ) : subsection.id === "emergency-contact-preparedness" ? (
            <PatientProfileEmergencyPreparednessSection
              actions={actions}
              patient={props.patient as PatientProfilePatient}
            />
          ) : subsection.id === "patient-contacts" ? (
            <PatientProfilePatientContactsSection
              actions={actions}
              patient={props.patient as PatientProfilePatient}
            />
          ) : subsection.id === "information" ? (
            <PatientProfileInformationSection
              actions={actions}
              patient={props.patient as PatientProfilePatient}
            />
          ) : subsection.id === "faxes" ? (
            <PatientProfileFaxesSection actions={actions} patientId={props.patientId} />
          ) : subsection.id === "patient-caregiver-relationships" ? (
            <PatientProfileCaregiverRelationshipsSection
              actions={actions}
              patient={props.patient}
              patientId={props.patientId}
            />
          ) : subsection.id === "contracts" ? (
            <PatientProfileContractsSection
              actions={actions}
              patient={props.patient}
              patientId={props.patientId}
            />
          ) : subsection.id === "authorizations" ? (
            <VStack alignItems="stretch" spacing={3}>
              {renderAddAuthorizationButton()}
              <PatientAuthorizationDetailsTable
                actions={actions}
                patient={props.patient}
                patientId={props.patientId}
                onDone={refreshPatient}
              />
            </VStack>
          ) : subsection.id === "diagnosis-codes" ? (
            <PatientProfileDiagnosisCodesSection
              actions={actions}
              contracts={patientContracts}
              patient={props.patient}
              patientId={props.patientId}
              onDone={refreshPatient}
            />
          ) : subsection.id === "pay-rates" ? (
            <PatientProfilePayRatesSection
              actions={actions}
              patient={props.patient}
              patientId={props.patientId}
              onDone={refreshPatient}
            />
          ) : subsection.id === "surpluses" ? (
            <PatientProfileSurplusesSection
              contracts={patientContracts}
              patientId={props.patientId}
            />
          ) : subsection.id === "billing" ? (
            <PatientProfileBillingInvoicesSection
              actions={actions}
              patient={props.patient}
              patientId={props.patientId}
            />
          ) : props.section.label === "Medical" ? (
            <PatientProfileMedicalSection
              actions={actions}
              patient={props.patient}
              patientId={props.patientId}
              sectionId={subsection.id}
            />
          ) : subsection.id === "recent-activity" ? (
            <PatientProfileRecentActivitySection actions={actions} patientId={props.patientId} />
          ) : (
            <PlaceholderSection />
          )}
        </EntityProfileSectionCard>
      ))}
    </>
  );
}
